<template>
  <div class="header-wrapper">
    <div v-if="authStore.isImpersonating" class="impersonation-banner">
      <span>Viewing as {{ authStore.displayName }} ({{ authStore.displayEmail }})</span>
      <button @click="stopImpersonating" class="stop-impersonating">Exit</button>
    </div>
    <header class="navigation-header">
      <div class="header-container">
        <!-- Logo -->
        <a :href="uploadUrl" class="logo" aria-label="Twig home">
          <img src="/twig-logo.svg" alt="Twig" height="28" />
          <span class="product">Dev Edit</span>
        </a>

        <!-- Navigation Menu -->
        <div v-if="authStore.isAuthenticated" class="nav-menu">
          <router-link to="/edits" class="nav-link">Edits</router-link>
          <span class="separator">|</span>
          <div class="dropdown-container">
            <button @click="toggleDropdown" class="dropdown-trigger" ref="dropdownTrigger">
              <span class="account-text">
                Account
                <span v-if="!authStore.emailVerified" class="verification-dot"></span>
              </span>
              <svg class="chevron" viewBox="0 0 20 20" fill="currentColor">
                <path
                  fill-rule="evenodd"
                  d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                  clip-rule="evenodd"
                />
              </svg>
            </button>

            <!-- Dropdown Menu -->
            <transition name="dropdown">
              <div v-if="isDropdownOpen" class="dropdown-menu" ref="dropdownMenu">
                <!-- User Info -->
                <div class="dropdown-section">
                  <div class="user-info">
                    <div class="user-name">{{ authStore.displayName || 'User' }}</div>
                    <div class="user-email">{{ authStore.displayEmail }}</div>
                  </div>
                </div>

                <div class="dropdown-divider"></div>

                <!-- Credit Balance -->
                <div class="dropdown-section">
                  <div class="credit-balance">
                    <svg class="credit-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z"
                      />
                    </svg>
                    <div class="credit-info">
                      <div class="credit-label">Edit Credits</div>
                      <div class="credit-value">{{ creditBalance }} available</div>
                    </div>
                    <router-link to="/credits" class="buy-credits-link" @click="closeDropdown">Buy</router-link>
                  </div>
                </div>

                <div class="dropdown-divider"></div>

                <!-- Support -->
                <div class="dropdown-section">
                  <button @click="openSupport" class="dropdown-item">
                    <svg class="item-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fill-rule="evenodd"
                        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Support
                  </button>
                </div>

                <div class="dropdown-divider"></div>

                <!-- Account Links -->
                <div class="dropdown-section">
                  <router-link to="/billing" class="dropdown-item" @click="closeDropdown">
                    <svg class="item-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4z" />
                      <path
                        fill-rule="evenodd"
                        d="M18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Billing
                  </router-link>

                  <router-link to="/account" class="dropdown-item" @click="closeDropdown">
                    <svg class="item-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fill-rule="evenodd"
                        d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Account Settings
                    <span v-if="!authStore.emailVerified" class="verification-badge">Verify Email</span>
                  </router-link>

                  <router-link
                    v-if="authStore.isAdmin && !authStore.isImpersonating"
                    to="/admin/users"
                    class="dropdown-item"
                    @click="closeDropdown"
                  >
                    <svg class="item-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z"
                      />
                    </svg>
                    Admin Panel
                  </router-link>

                  <button @click="handleSignOut" class="dropdown-item">
                    <svg class="item-icon" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fill-rule="evenodd"
                        d="M3 3a1 1 0 00-1 1v12a1 1 0 102 0V4a1 1 0 00-1-1zm10.293 9.293a1 1 0 001.414 1.414l3-3a1 1 0 000-1.414l-3-3a1 1 0 10-1.414 1.414L14.586 9H7a1 1 0 100 2h7.586l-1.293 1.293z"
                        clip-rule="evenodd"
                      />
                    </svg>
                    Sign Out
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Sign In Button (when not authenticated, and not already on the sign-in page) -->
        <router-link v-else-if="route.name !== 'auth'" to="/auth" class="sign-in-button"> Sign In </router-link>
      </div>
    </header>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { fetchWithAuth } from '../services/api';
import { useAuthStore } from '../stores/auth';

const router = useRouter();
const route = useRoute();
const authStore = useAuthStore();
const isDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const uploadUrl = isDev ? '/' : 'https://twig.io/dev-edit';

const isDropdownOpen = ref(false);
const dropdownTrigger = ref<HTMLButtonElement>();
const dropdownMenu = ref<HTMLDivElement>();
const creditBalance = ref(0);

function toggleDropdown() {
  isDropdownOpen.value = !isDropdownOpen.value;
}

function closeDropdown() {
  isDropdownOpen.value = false;
}

function openSupport() {
  if (window.Beacon) {
    window.Beacon('open');
  }
  closeDropdown();
}

async function handleSignOut() {
  await authStore.signOut();
  closeDropdown();
  router.push('/auth');
}

function stopImpersonating() {
  sessionStorage.removeItem('impersonate');
  window.close();
}

async function loadCreditBalance(retryCount = 0) {
  if (!authStore.uid) {
    return;
  }

  try {
    const response = await fetchWithAuth(`/api/users/${authStore.uid}/credits`);

    // If we get 401 and haven't retried yet, wait and retry (auth token might not be ready)
    if (response.status === 401 && retryCount < 2) {
      setTimeout(() => loadCreditBalance(retryCount + 1), 1000);
      return;
    }

    if (!response.ok) {
      return;
    }

    const data = await response.json();
    creditBalance.value = data.balance || 0;
  } catch (error) {
    console.error('[NavigationHeader] Failed to load credit balance:', error);
  }
}

// Click outside to close dropdown
function handleClickOutside(event: MouseEvent) {
  if (
    isDropdownOpen.value &&
    dropdownTrigger.value &&
    dropdownMenu.value &&
    !dropdownTrigger.value.contains(event.target as Node) &&
    !dropdownMenu.value.contains(event.target as Node)
  ) {
    closeDropdown();
  }
}

// Wait for auth to finish loading, then load credits once
watch(
  () => authStore.loading,
  loading => {
    if (!loading && authStore.uid) {
      loadCreditBalance();
    }
  }
);

// Also watch for uid changes (user logs in after mount)
watch(
  () => authStore.uid,
  newUid => {
    if (newUid && !authStore.loading) {
      loadCreditBalance();
    }
  }
);

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  // Only load immediately if auth is already ready
  if (!authStore.loading && authStore.uid) {
    loadCreditBalance();
  }
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
.header-wrapper {
  position: sticky;
  top: 0;
  z-index: 50;
}

.navigation-header {
  background: white;
  border-bottom: 1px solid #e2e8f0;
}

.header-container {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 1.5rem;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  text-decoration: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  height: 100%;
  padding: 0 0.75rem;
  margin-left: -0.75rem;
  color: #1a202c;
}

/* The same lockup as arcs.twig.io: wordmark, thin rule, product name in PT Serif. */
.product {
  font-family: 'PT Serif', Georgia, 'Times New Roman', serif;
  font-size: 18px;
  line-height: 1;
  padding-left: 0.75rem;
  border-left: 1px solid #cbd5e0;
  white-space: nowrap;
}

@media (max-width: 480px) {
  .product {
    display: none;
  }
}

.nav-menu {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.nav-link {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
  text-decoration: none;
  transition: color 0.2s;
  padding: 0.25rem 0;
}

.nav-link:hover {
  color: #1a202c;
}

.dropdown-container {
  position: relative;
}

.dropdown-trigger {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.25rem 0;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a5568;
  background: none;
  border: none;
  cursor: pointer;
  transition: color 0.2s;
  font-family: inherit;
  position: relative;
}

.dropdown-trigger:hover {
  color: #1a202c;
}

.separator {
  color: #cbd5e0;
  font-size: 0.875rem;
}

.account-text {
  position: relative;
}

.verification-dot {
  width: 8px;
  height: 8px;
  background: #e53e3e;
  border-radius: 50%;
  position: absolute;
  top: -2px;
  right: -8px;
  box-shadow: 0 0 0 2px white;
}

.chevron {
  width: 16px;
  height: 16px;
  color: #a0aec0;
}

.dropdown-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 0.5rem);
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  min-width: 260px;
  overflow: hidden;
}

.dropdown-section {
  padding: 0.5rem 0;
}

.dropdown-divider {
  height: 1px;
  background: #e2e8f0;
}

.user-info {
  padding: 0 1rem;
}

.user-name {
  font-weight: 600;
  color: #1a202c;
  font-size: 0.875rem;
}

.user-email {
  color: #718096;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.credit-balance {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0 1rem;
}

.credit-icon {
  width: 24px;
  height: 24px;
  color: var(--color-accent);
  flex-shrink: 0;
}

.credit-info {
  flex: 1;
  min-width: 0;
}

.credit-label {
  font-size: 0.75rem;
  color: #718096;
  font-weight: 500;
}

.credit-value {
  font-size: 0.875rem;
  color: #1a202c;
  font-weight: 600;
  margin-top: 0.125rem;
}

.buy-credits-link {
  padding: 0.375rem 0.875rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--color-accent);
  background: white;
  border: 1.5px solid var(--color-accent);
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.2s;
  flex-shrink: 0;
}

.buy-credits-link:hover {
  background: var(--color-accent);
  color: white;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  width: 100%;
  padding: 0.625rem 1rem;
  font-size: 0.875rem;
  color: #4a5568;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
  text-decoration: none;
  font-family: inherit;
  position: relative;
}

.dropdown-item:hover {
  background: #f7fafc;
}

.item-icon {
  width: 18px;
  height: 18px;
  color: #a0aec0;
}

.verification-badge {
  margin-left: auto;
  padding: 0.25rem 0.5rem;
  font-size: 0.625rem;
  font-weight: 600;
  color: #e53e3e;
  background: #fff5f5;
  border-radius: 4px;
  border: 1px solid #feb2b2;
}

.sign-in-button {
  padding: 0.5rem 1.5rem;
  font-size: 0.875rem;
  font-weight: 600;
  color: white;
  background: var(--color-accent);
  border-radius: 6px;
  text-decoration: none;
  transition: all 0.2s;
}

.sign-in-button:hover {
  background: var(--color-accent-hover);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(97, 174, 212, 0.3);
}

/* Dropdown transition */
.dropdown-enter-active,
.dropdown-leave-active {
  transition: all 0.15s ease-out;
}

.dropdown-enter-from {
  opacity: 0;
  transform: translateY(-8px);
}

.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

/* Impersonation banner */
.impersonation-banner {
  background: #fef5e7;
  border-bottom: 1px solid #f9e79f;
  padding: 0.5rem 1.5rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
}

.impersonation-banner span {
  font-size: 0.875rem;
  font-weight: 600;
  color: #7d6608;
}

.stop-impersonating {
  padding: 0.25rem 0.75rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: #7d6608;
  background: white;
  border: 1px solid #f9e79f;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.stop-impersonating:hover {
  background: #fef5e7;
  border-color: #f4d03f;
}
</style>
